'use client'

import { useMemo, type ReactNode } from 'react'
import { useDashboardData } from '@/hooks/useDashboardData'
import { useFilteredData } from '@/hooks/useFilters'
import { usePlanReliability } from '@/hooks/usePlanReliability'
import {
  computeOverdueTasks, computeEngineerCapacity, computeInsights, isRecurring,
} from '@/lib/calculations'
import type { EngineerCapacity } from '@/lib/calculations'
import { formatRelativeTime, syncFreshness, getWeekKey } from '@/lib/dates'
import Badge from '@/components/ui/Badge'
import type { Task } from '@/types'

type CapacityTask = EngineerCapacity['tasks'][number]

const MAX_INSIGHTS = 5
const MAX_BACKLOG_ROWS = 8

// Odoo's stage names vary a lot per project (dozens of custom kanban columns), so this
// colors by keyword rather than an exact-match list — "blocked" always reads as risk
// regardless of which project's custom stage name it appears in.
function stageBadgeClass(stage: string): string {
  const s = stage.toLowerCase()
  if (s.includes('block')) return 'bg-red-50 text-red-700 dark:bg-red-950/50 dark:text-red-300'
  if (s.includes('progress')) return 'bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300'
  return 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
}

const FRESHNESS_STYLES = {
  good: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-400',
  warning: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-400',
  bad: 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-400',
  unknown: 'border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-800 dark:bg-slate-800/30 dark:text-slate-400',
}
const FRESHNESS_DOT = { good: 'bg-emerald-500', warning: 'bg-amber-500', bad: 'bg-red-500', unknown: 'bg-slate-400' }

function TaskRow({ task, odooUrl, right }: { task: Task; odooUrl: string; right: ReactNode }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2 border-b border-slate-100 dark:border-slate-800/50 last:border-0 text-sm">
      <a href={`${odooUrl}/odoo/project.project/${task.projId}/tasks/${task.id}`} target="_blank" rel="noopener noreferrer"
        className="flex-1 truncate text-slate-900 dark:text-slate-100 hover:underline font-medium" title={task.name}>
        {task.name}
      </a>
      <span className="text-xs text-slate-400 truncate max-w-[120px]">{task.users.join(', ') || 'Unassigned'}</span>
      {right}
    </div>
  )
}

// A task with an approaching or blown deadline is exactly the kind of thing "is
// this engineer actually making progress" should surface — a bare Est/Worked/Left
// doesn't say anything about urgency on its own.
function deadlineInfo(deadline: string): { label: string; variant: 'danger' | 'warning' | 'default' } | null {
  if (!deadline) return null
  const days = Math.ceil((new Date(deadline).getTime() - Date.now()) / 86400000)
  if (days < 0) return { label: `${Math.abs(days)}d overdue`, variant: 'danger' }
  if (days <= 5) return { label: `due ${days}d`, variant: 'warning' }
  return { label: new Date(deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), variant: 'default' }
}

// One row in an engineer's capacity list — shared by the In Progress, Queued, and
// Backlog groups so the deadline/subtask-progress signal shows up everywhere, not
// just for "counted" work.
function CapacityTaskRow({ t, odooUrl, showStage = true, showWorked = true }: {
  t: CapacityTask; odooUrl: string; showStage?: boolean; showWorked?: boolean
}) {
  const dl = deadlineInfo(t.deadline)
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 border-b border-slate-100 dark:border-slate-800/50 last:border-0">
      {showStage && (
        <span className={`text-[0.65rem] px-1.5 py-0.5 font-semibold shrink-0 ${stageBadgeClass(t.stage)}`}>
          {t.stage}
        </span>
      )}
      <a href={`${odooUrl}/odoo/project.project/${t.projId}/tasks/${t.id}`} target="_blank" rel="noopener noreferrer"
        title={t.name} className="flex-1 truncate text-sm text-slate-900 dark:text-slate-100 hover:underline">
        {t.name}
        {t.childTotal > 0 && (
          <span className="ml-1.5 text-[0.65rem] text-slate-400">{t.childDone}/{t.childTotal} subtasks done</span>
        )}
      </a>
      {dl && <Badge variant={dl.variant} className="shrink-0">{dl.label}</Badge>}
      <span className="text-xs text-slate-400 shrink-0 text-right leading-tight">
        <span className="block">Est: {t.totalEstimation > 0 ? `${t.totalEstimation.toFixed(1)}h` : '—'}</span>
        {showWorked && <span className="block">Worked: {t.workedThisWeek.toFixed(1)}h</span>}
        <span className="block">Left: {t.leftInEstimation !== null ? `${t.leftInEstimation.toFixed(1)}h` : '—'}</span>
      </span>
    </div>
  )
}

export default function DashboardPage() {
  const { isLoading, activeEngineers, updatedAt, odooUrl, timesheets, tasks, activeProjectIds } = useDashboardData()
  const { filteredTasks, activeTasks } = useFilteredData()
  const planReliability = usePlanReliability(filteredTasks)

  // Recurring maintenance tasks (tagged "AWS Recurring") are excluded everywhere on
  // this page, same as the Delivery page — they aren't real delivery work.
  const regularActiveTasks = useMemo(() => activeTasks.filter(t => !isRecurring(t)), [activeTasks])
  const regularFilteredTasks = useMemo(() => filteredTasks.filter(t => !isRecurring(t)), [filteredTasks])

  const overdueTasks = useMemo(() => computeOverdueTasks(regularActiveTasks), [regularActiveTasks])

  // Everything not finished yet, any stage — Odoo's stage names vary a lot per
  // project (dozens of project-specific custom stages), so this isn't narrowed to a
  // fixed list of "active-looking" stage names. Blocked tasks are excluded from
  // capacity specifically: their full remaining estimate isn't realistically
  // consuming this week's time while they're stuck waiting on something, so counting
  // it against free capacity produces impossible "hundreds of hours over" numbers.
  const capacityTasks = useMemo(
    () => regularFilteredTasks.filter(t => t.stage !== 'Done' && !t.stage.toLowerCase().includes('block')),
    [regularFilteredTasks],
  )
  const currentWeekKey = useMemo(() => getWeekKey(0), [])

  // Unassigned subtasks' hours roll up into their parent task's assignee (see
  // computeEngineerCapacity) — this pool skips the header's engineer filter so
  // switching that dropdown never hides another engineer's unassigned subtasks
  // out from under a parent task.
  const capacitySubtaskPool = useMemo(
    () => tasks.filter(t => activeProjectIds.includes(t.projId) && !t.cancelled && !isRecurring(t)
      && t.stage !== 'Done' && !t.stage.toLowerCase().includes('block')),
    [tasks, activeProjectIds],
  )
  const engineerCapacity = useMemo(
    () => computeEngineerCapacity(activeEngineers, capacityTasks, timesheets, currentWeekKey, capacitySubtaskPool),
    [activeEngineers, capacityTasks, timesheets, currentWeekKey, capacitySubtaskPool],
  )

  // Same split used by the detailed per-engineer cards below, lifted up so the
  // glance-level summary table above them doesn't recompute it separately.
  const capacitySummary = useMemo(() => engineerCapacity.map(ec => {
    const counted = ec.tasks.filter(t => t.countsTowardCapacity)
    const inProgress = counted.filter(t => t.stage.toLowerCase().includes('progress'))
    const queued = counted.filter(t => !t.stage.toLowerCase().includes('progress'))
    const backlog = ec.tasks.filter(t => !t.countsTowardCapacity)
    const backlogHours = backlog.reduce((a, t) => a + (t.leftInEstimation || 0), 0)
    const stalled = inProgress.length > 0 && ec.loggedThisWeek < 1
    return { ec, inProgress, queued, backlog, backlogHours, stalled }
  }), [engineerCapacity])

  // Concrete, current-state findings (blocked tasks, capacity imbalance, backlog
  // pileup, stalled work, thin margins) — not period-over-period metric deltas,
  // which barely moved day to day and gave no one anything to actually do.
  const insights = useMemo(
    () => computeInsights(regularFilteredTasks, engineerCapacity, timesheets).slice(0, MAX_INSIGHTS),
    [regularFilteredTasks, engineerCapacity, timesheets],
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400">
        Loading dashboard...
      </div>
    )
  }

  const freshness = syncFreshness(updatedAt)

  return (
    <div className="space-y-6">
      {/* Work-in-progress notice */}
      <div className="px-4 py-2 text-xs border border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">
        Note: still in progress. More work needed on Margins, Billable Utilization, and Capacity Planning.
      </div>

      {/* Data freshness */}
      <div className={`flex items-center gap-2 px-4 py-2 text-xs border ${FRESHNESS_STYLES[freshness]}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${FRESHNESS_DOT[freshness]}`} />
        <span>{updatedAt ? `Data as of ${formatRelativeTime(updatedAt)}` : 'Sync status unavailable'}</span>
        {freshness === 'bad' && <span>— older than expected, run /health-check</span>}
      </div>

      {/* Deadlines */}
      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
        <h3 className="text-base font-semibold mb-1">
          Deadlines <span className="text-xs font-normal text-slate-400">{overdueTasks.length} overdue</span>
        </h3>
        {overdueTasks.length === 0 ? (
          <p className="text-sm text-slate-400 py-3">Nothing overdue right now.</p>
        ) : (
          <>
            <div className="border border-slate-200 dark:border-slate-800 mt-3">
              {overdueTasks.slice(0, 5).map(o => (
                <TaskRow key={o.task.id} task={o.task} odooUrl={odooUrl} right={
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Badge variant="danger">Overdue</Badge>
                    <span className="text-xs text-slate-400 w-20 text-right">{o.daysOverdue}d</span>
                  </div>
                } />
              ))}
            </div>
            {overdueTasks.length > 5 && (
              <a href="/dashboard/delivery" className="text-xs text-blue-600 hover:underline mt-2 inline-block">
                +{overdueTasks.length - 5} more → see all on Delivery
              </a>
            )}
          </>
        )}
      </section>

      {/* Capacity */}
      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
        <h3 className="text-base font-semibold mb-1">Capacity</h3>
        <div className="text-sm mb-4">
          Plan Reliability:{' '}
          {planReliability !== null ? (
            <span className={`font-semibold ${planReliability >= 80 ? 'text-emerald-600' : planReliability >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
              {planReliability.toFixed(0)}%
            </span>
          ) : (
            <span className="text-slate-400">no data</span>
          )}
          <span className="text-slate-400"> of last week&apos;s plan completed — </span>
          <a href="/dashboard/planning" className="text-blue-600 hover:underline">Planning</a>
        </div>

        <div className="text-xs uppercase tracking-wider text-slate-400 mb-2 font-[family-name:var(--font-display)]">
          At a Glance
        </div>
        <div className="overflow-x-auto mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800">
                {['Engineer', 'Capacity', 'Logged wk', 'Working on now', 'Queued', 'Backlog'].map(h => (
                  <th key={h} className="text-left px-2 py-1 text-xs uppercase tracking-wider text-slate-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {capacitySummary.map(({ ec, inProgress, queued, backlog, backlogHours, stalled }) => (
                <tr key={ec.name} className="border-b border-slate-100 dark:border-slate-800/50 align-top">
                  <td className="px-2 py-1.5 font-medium whitespace-nowrap">{ec.name}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <Badge variant={ec.freeHours < 0 ? 'danger' : ec.freeHours <= 10 ? 'warning' : 'success'}>
                      {ec.freeHours >= 0 ? `${ec.freeHours.toFixed(0)}h free` : `${Math.abs(ec.freeHours).toFixed(0)}h over`}
                    </Badge>
                  </td>
                  <td className={`px-2 py-1.5 whitespace-nowrap ${stalled ? 'text-amber-600 dark:text-amber-400 font-medium' : 'text-slate-400'}`}>
                    {ec.loggedThisWeek.toFixed(1)}h
                  </td>
                  <td className="px-2 py-1.5 min-w-[220px]">
                    {inProgress.length === 0 ? (
                      <span className="text-slate-400 italic">Nothing in progress</span>
                    ) : (
                      <div className="space-y-0.5">
                        {inProgress.map(t => {
                          const dl = deadlineInfo(t.deadline)
                          return (
                            <div key={t.id} className="flex items-center gap-1.5">
                              <a href={`${odooUrl}/odoo/project.project/${t.projId}/tasks/${t.id}`} target="_blank" rel="noopener noreferrer"
                                title={t.name} className="truncate max-w-[260px] text-slate-900 dark:text-slate-100 hover:underline">
                                {t.name}
                              </a>
                              {t.childTotal > 0 && (
                                <span className="text-xs text-slate-400 shrink-0">({t.childDone}/{t.childTotal})</span>
                              )}
                              {dl && dl.variant !== 'default' && (
                                <Badge variant={dl.variant} className="shrink-0 !px-1 !py-0 text-[0.6rem]">{dl.label}</Badge>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-slate-400 whitespace-nowrap">{queued.length}</td>
                  <td className="px-2 py-1.5 text-slate-400 whitespace-nowrap">
                    {backlog.length}{backlog.length > 0 && ` (${backlogHours.toFixed(0)}h)`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="text-xs uppercase tracking-wider text-slate-400 mb-2 font-[family-name:var(--font-display)]">
          Capacity by Engineer <span className="normal-case text-slate-400">(full detail)</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
          {capacitySummary.map(({ ec, inProgress, queued, backlog, backlogHours, stalled }) => {
            return (
              <div key={ec.name}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold">{ec.name}</span>
                  <Badge variant={ec.freeHours < 0 ? 'danger' : ec.freeHours <= 10 ? 'warning' : 'success'}>
                    {ec.freeHours >= 0 ? `${ec.freeHours.toFixed(0)}h free` : `${Math.abs(ec.freeHours).toFixed(0)}h over`}
                  </Badge>
                </div>
                <p className="text-xs text-slate-400 mb-1.5">
                  {ec.remainingCapacity.toFixed(0)}h this week − {ec.totalLeftInEstimation.toFixed(1)}h left in estimation (excludes Backlog)
                  {' · '}
                  <span className={stalled ? 'text-amber-600 dark:text-amber-400 font-medium' : ''}>
                    {ec.loggedThisWeek.toFixed(1)}h logged this week
                  </span>
                </p>
                {inProgress.length === 0 && queued.length === 0 && backlog.length === 0 ? (
                  <p className="text-xs text-slate-400">Nothing open right now.</p>
                ) : (
                  <>
                    {inProgress.length > 0 && (
                      <div className="mb-1.5">
                        <p className="text-[0.65rem] uppercase tracking-wider text-slate-400 mb-0.5">In Progress</p>
                        <div className="border border-slate-200 dark:border-slate-800">
                          {inProgress.map(t => <CapacityTaskRow key={t.id} t={t} odooUrl={odooUrl} />)}
                        </div>
                      </div>
                    )}
                    {queued.length > 0 && (
                      <div className="mb-1.5">
                        <p className="text-[0.65rem] uppercase tracking-wider text-slate-400 mb-0.5">Queued</p>
                        <div className="border border-slate-200 dark:border-slate-800">
                          {queued.map(t => <CapacityTaskRow key={t.id} t={t} odooUrl={odooUrl} />)}
                        </div>
                      </div>
                    )}
                    {backlog.length > 0 && (
                      <div>
                        <p className="text-xs text-slate-400 mb-1">
                          {backlog.length} in Backlog ({backlogHours.toFixed(0)}h estimated, not counted toward capacity):
                        </p>
                        <div className="border border-slate-200 dark:border-slate-800">
                          {backlog.slice(0, MAX_BACKLOG_ROWS).map(t => (
                            <CapacityTaskRow key={t.id} t={t} odooUrl={odooUrl} showStage={false} showWorked={false} />
                          ))}
                          {backlog.length > MAX_BACKLOG_ROWS && (
                            <p className="text-xs text-slate-400 px-3 py-1.5">+{backlog.length - MAX_BACKLOG_ROWS} more not shown.</p>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )
          })}
        </div>
      </section>

      {/* Insights */}
      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
        <h3 className="text-base font-semibold mb-3">
          Insights <span className="text-xs font-normal text-slate-400">as of {updatedAt ? formatRelativeTime(updatedAt) : 'today'}</span>
        </h3>
        {insights.length === 0 ? (
          <p className="text-sm text-slate-400 py-3">Nothing urgent right now.</p>
        ) : (
          <div className="space-y-4">
            {insights.map(i => (
              <div key={i.key} className="border-l-2 border-slate-200 dark:border-slate-700 pl-3">
                <p className="text-sm font-semibold leading-snug text-slate-900 dark:text-slate-100">
                  {i.headline}
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">→ {i.recommendation}</p>
                {i.tasks.length > 0 && (
                  <ul className="mt-1.5 space-y-0.5">
                    {i.tasks.map(t => (
                      <li key={t.id} className="text-xs flex items-center gap-1.5">
                        <a href={`${odooUrl}/odoo/project.project/${t.projId}/tasks/${t.id}`} target="_blank" rel="noopener noreferrer"
                          className="text-slate-700 dark:text-slate-300 hover:underline truncate max-w-[280px]" title={t.name}>
                          {t.name}
                        </a>
                        <span className="text-slate-400 shrink-0">— {t.meta}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
